import React, { useEffect, useMemo, useState } from 'react';
import { ToastList } from '@devesharp/react/web';
import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { useParams } from 'react-router-dom';
import { Observable } from 'rxjs';
import {
   CompanyIdezGetResolve,
   EstablishmentIdezBiometricsResendResolve,
   EstablishmentIdezCreateResolve,
   EstablishmentIdezDocumentRetryResolve,
   EstablishmentIdezDocumentStagingResolve,
   EstablishmentIdezDocumentUploadResolve,
   IdezOccupationsResolve,
} from '~/api';
import history from '~/config/history';
import {
   ALL_IDEZ_DOC_TYPES,
   biometricsStatusLabel,
   buildDocumentFields,
   buildDocumentSections,
   IdezBiometricEntity,
   openBiometricsUrl,
   socialContractHint,
} from '~/pages/EstablishmentIdez/EstablishmentIdezPage.utils';

const ESTABLISHMENT_TYPES = [
   { value: 'EI', name: 'EI' },
   { value: 'MEI', name: 'MEI' },
   { value: 'LTDA', name: 'LTDA' },
   { value: 'SS', name: 'SS' },
   { value: 'SA', name: 'SA' },
   { value: 'ME', name: 'ME' },
   { value: 'EPP', name: 'EPP' },
   { value: 'EMGP', name: 'EMGP' },
   { value: 'COOP', name: 'COOP' },
   { value: 'DEMAIS', name: 'DEMAIS' },
];

const IDENTITY_TYPES = [
   { value: 'rg', name: 'RG' },
   { value: 'cnh', name: 'CNH' },
];

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_MIME_PREFIXES = ['image/', 'application/pdf'];

function toPromise<T>(observable: Observable<T>): Promise<T> {
   return new Promise((resolve, reject) => {
      observable.subscribe({
         next: resolve,
         error: reject,
      });
   });
}

function isAllowedFile(file: File): boolean {
   return ALLOWED_MIME_PREFIXES.some((prefix) => file.type.startsWith(prefix));
}

function buildValidationSchema(isBusiness: boolean) {
   const base = {
      password: Yup.string().required('Senha Idez é obrigatória').min(8, 'Mínimo 8 caracteres'),
      pin: Yup.string().required('PIN é obrigatório').length(4, 'PIN deve ter 4 dígitos'),
   };

   if (isBusiness) {
      return Yup.object().shape({
         ...base,
         legal_nature: Yup.string().required('Natureza jurídica é obrigatória'),
         establishment_type: Yup.string().required('Tipo de estabelecimento é obrigatório'),
         main_cnae: Yup.string().required('CNAE principal é obrigatório'),
         partner_mother_name: Yup.string().required('Nome da mãe do sócio é obrigatório'),
         partner_identity_type: Yup.string().required('Tipo do documento do sócio é obrigatório'),
         partner_identity_number: Yup.string().required('Número do documento do sócio é obrigatório'),
         partner_identity_issued_at: Yup.string().required('Data de emissão do documento do sócio é obrigatória'),
         partner_monthly_income: Yup.number().typeError('Renda mensal inválida').required('Renda mensal do sócio é obrigatória'),
      });
   }

   return Yup.object().shape({
      ...base,
      mother_name: Yup.string().required('Nome da mãe é obrigatório'),
      identity_type: Yup.string().required('Tipo do documento é obrigatório'),
      identity_number: Yup.string().required('Número do documento é obrigatório'),
      identity_issued_at: Yup.string().required('Data de emissão é obrigatória'),
      occupation_id: Yup.string().required('Profissão é obrigatória'),
      monthly_income: Yup.number().typeError('Renda mensal inválida').required('Renda mensal é obrigatória'),
   });
}

export function useEstablishmentIdezPage(formRef: React.RefObject<FormHandles>): any {
   const { companyId } = useParams<any>();
   const [company, setCompany] = useState<any>(null);
   const [occupations, setOccupations] = useState<any[]>([]);
   const [loadingCompany, setLoadingCompany] = useState(true);
   const [saving, setSaving] = useState(false);
   const [resendingEntity, setResendingEntity] = useState<string | null>(null);
   const [pendingFiles, setPendingFiles] = useState<Record<string, File | null>>({});
   const [documentErrors, setDocumentErrors] = useState<Record<string, string>>({});
   const [establishmentType, setEstablishmentType] = useState<string>('');

   const hasIdezAccount = Boolean(company?.has_idez_account ?? company?.has_idez);
   const isBusiness = company?.type === 'business';
   const idezBiometrics: IdezBiometricEntity[] = company?.idez_biometrics ?? [];
   const accountIsActive = String(company?.idez_status ?? '').toLowerCase() === 'active';

   const existingDocuments = useMemo(() => {
      const map: Record<string, any> = {};
      (company?.documents ?? []).forEach((doc: any) => {
         map[doc.type] = doc;
      });
      return map;
   }, [company?.documents]);

   const documentSections = useMemo(
      () => buildDocumentSections(company?.kyc_requirements, isBusiness, establishmentType),
      [company?.kyc_requirements, isBusiness, establishmentType],
   );

   const documentFields = useMemo(
      () => buildDocumentFields(documentSections, existingDocuments, pendingFiles, documentErrors),
      [documentSections, existingDocuments, pendingFiles, documentErrors],
   );

   const documentsHint = socialContractHint(isBusiness, establishmentType);

   document.title = hasIdezAccount ? 'Gerenciar Idez - XPAG Tecnologia' : 'Cadastro Idez - XPAG Tecnologia';

   function reloadCompany() {
      CompanyIdezGetResolve(companyId).subscribe(
         (companyData) => {
            setCompany(companyData);
            setEstablishmentType(
               companyData?.kyc_requirements?.establishment_type ??
                  companyData?.establishment_type ??
                  '',
            );
         },
         () => {},
      );
   }

   useEffect(() => {
      let pendingRequests = 2;

      function finishLoading() {
         pendingRequests -= 1;
         if (pendingRequests <= 0) {
            setLoadingCompany(false);
         }
      }

      CompanyIdezGetResolve(companyId).subscribe(
         (companyData) => {
            setCompany(companyData);
            setEstablishmentType(
               companyData?.kyc_requirements?.establishment_type ??
                  companyData?.establishment_type ??
                  '',
            );

            if (formRef.current) {
               formRef.current.setData({
                  is_pep: false,
                  is_foreign: false,
                  partner_is_pep: false,
                  ...companyData,
               });
            }

            finishLoading();
         },
         () => {
            ToastList.show({
               message: 'Não foi possível carregar o estabelecimento',
               type: 'error',
            });
            history.push('/establishment');
            finishLoading();
         },
      );

      IdezOccupationsResolve(companyId).subscribe(
         (items) => {
            const list = Array.isArray(items) ? items : items?.results ?? [];
            setOccupations(list);
            finishLoading();
         },
         () => finishLoading(),
      );
   }, [companyId, formRef]);

   function onEstablishmentTypeChange(value: string) {
      setEstablishmentType(value ?? '');
   }

   function onDocumentChange(type: string, file?: File | null) {
      const errors = { ...documentErrors };
      delete errors[type];

      if (!file) {
         setPendingFiles((current) => ({ ...current, [type]: null }));
         setDocumentErrors(errors);
         return;
      }

      if (file.size > MAX_FILE_SIZE) {
         errors[type] = 'Arquivo deve ter no máximo 5MB';
         setDocumentErrors(errors);
         setPendingFiles((current) => ({ ...current, [type]: null }));
         return;
      }

      if (!isAllowedFile(file)) {
         errors[type] = 'Formato inválido. Use PNG, JPEG, PDF ou similar';
         setDocumentErrors(errors);
         setPendingFiles((current) => ({ ...current, [type]: null }));
         return;
      }

      setPendingFiles((current) => ({ ...current, [type]: file }));
      setDocumentErrors(errors);

      if (!hasIdezAccount) {
         EstablishmentIdezDocumentStagingResolve(companyId, type, file).subscribe({
            error: () => {
               setDocumentErrors((current) => ({
                  ...current,
                  [type]: 'Erro ao salvar documento no servidor',
               }));
            },
         });
      }
   }

   async function uploadPendingDocuments(): Promise<void> {
      for (const type of ALL_IDEZ_DOC_TYPES) {
         const file = pendingFiles[type];
         if (!file) {
            continue;
         }

         await toPromise(EstablishmentIdezDocumentUploadResolve(companyId, type, file));
      }
   }

   async function retryDocument(documentId: number): Promise<void> {
      await toPromise(EstablishmentIdezDocumentRetryResolve(companyId, documentId));
   }

   function validateBiometrics(entity: IdezBiometricEntity) {
      if (entity.biometrics_url) {
         openBiometricsUrl(entity.biometrics_url);
      }
   }

   async function resendBiometrics(entity: IdezBiometricEntity) {
      setResendingEntity(entity.entity_id);

      try {
         const sessions = await toPromise(
            EstablishmentIdezBiometricsResendResolve(companyId, {
               entity_document: entity.document ?? undefined,
            }),
         );

         const updated = Array.isArray(sessions)
            ? sessions.find((item: IdezBiometricEntity) => item.entity_id === entity.entity_id) ??
              sessions[0]
            : null;

         reloadCompany();

         if (updated?.biometrics_url) {
            openBiometricsUrl(updated.biometrics_url);
            ToastList.show({
               message: 'Nova sessão de biometria gerada',
               type: 'success',
            });
         } else {
            ToastList.show({
               message: 'Sessão renovada. Atualize a página se o link não abrir.',
               type: 'info',
            });
         }
      } catch (error: any) {
         ToastList.show({
            message: error?.data?.error ?? error?.data?.data?.error ?? 'Erro ao renovar sessão de biometria',
            type: 'error',
         });
      } finally {
         setResendingEntity(null);
      }
   }

   async function onSubmit(data: any) {
      if (hasIdezAccount) {
         const failedDocs = (company?.documents ?? []).filter(
            (doc: any) => doc.can_retry && doc.upload_status === 'failed',
         );

         const hasPending = Object.values(pendingFiles).some(Boolean);

         if (!hasPending && failedDocs.length === 0) {
            ToastList.show({
               message: 'Selecione o contrato social para enviar ou reenviar',
               type: 'error',
            });
            return;
         }

         setSaving(true);

         try {
            for (const doc of failedDocs) {
               await retryDocument(doc.id);
            }

            await uploadPendingDocuments();

            ToastList.show({
               message: 'Documento enviado com sucesso',
               type: 'success',
            });
            history.push(`/establishment/detail/${companyId}`);
         } catch (error: any) {
            ToastList.show({
               message: error?.data?.error ?? error?.data?.data?.error ?? 'Erro ao enviar documento',
               type: 'error',
            });
         } finally {
            setSaving(false);
         }

         return;
      }

      try {
         await buildValidationSchema(isBusiness).validate(data, { abortEarly: false });
      } catch (validationError: any) {
         validationError.inner?.forEach((err: any) => {
            formRef.current?.setFieldError(err.path, err.message);
         });
         return;
      }

      setSaving(true);

      try {
         await toPromise(EstablishmentIdezCreateResolve(companyId, data));

         ToastList.show({
            message: 'Conta Idez criada. Conclua a validação de identidade pela biometria.',
            type: 'success',
         });
         history.push(`/establishment/detail/${companyId}`);
      } catch (error: any) {
         ToastList.show({
            message: error?.data?.error ?? error?.data?.data?.error ?? 'Erro ao criar conta na Idez',
            type: 'error',
         });
      } finally {
         setSaving(false);
      }
   }

   const formInfoData = {
      occupations: occupations.map((item) => ({
         value: item.id,
         name: item.name ?? item.title ?? item.id,
      })),
      establishmentTypes: ESTABLISHMENT_TYPES,
      identityTypes: IDENTITY_TYPES,
   };

   const readonlyFields = company?.readonly_fields ?? {};

   const groupedDocumentFields = useMemo(() => {
      const groups: Record<string, any> = {};

      documentFields.forEach((field) => {
         if (!groups[field.sectionKey]) {
            groups[field.sectionKey] = {
               key: field.sectionKey,
               title: field.sectionTitle,
               required: field.sectionRequired,
               fields: [],
            };
         }

         groups[field.sectionKey].fields.push(field);
      });

      return Object.values(groups);
   }, [documentFields]);

   return {
      onSubmit,
      onDocumentChange,
      onEstablishmentTypeChange,
      retryDocument,
      validateBiometrics,
      resendBiometrics,
      biometricsStatusLabel,
      companyId,
      company,
      readonlyFields,
      loadingCompany,
      saving,
      resendingEntity,
      formInfoData,
      hasIdezAccount,
      isBusiness,
      accountIsActive,
      idezBiometrics,
      documentFields,
      groupedDocumentFields,
      documentsHint,
   };
}
